import { h } from '../lib/dom.js';
import { getState, setState, CONFIG } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import {
  togglePrivateKey, openImportKey, goLock, setTheme,
  setTestnetEnabled, setDefaultStablecoin, setDefaultChain,
  startProfileEdit, setProfileEditDraft, confirmProfileEdit, cancelProfileEdit,
  toggleRecoveryInfo, setProfileAvatar, copyLlmsTxt
} from '../actions.js';

// Icon toggle-right (blue, on) / toggle-left (grey, off) switches, per
// feature-ui.md's explicit follow-up spec — a real <button> (not a bare
// icon) so it stays a focusable, keyboard-activatable control.
function toggleButton(label, on, onclick) {
  return h('button', {
    class: 'icon-btn-plain', 'aria-label': label, 'aria-pressed': on ? 'true' : 'false', onclick
  }, h('span', { html: iconMarkup(on ? 'toggleRight' : 'toggleLeft', { size: 28, color: on ? '#4D4DFF' : '#757993' }) }));
}

function profileField(s, p, field, label) {
  const editing = s.profileEdit.profileId === p.id && s.profileEdit.field === field;
  if (editing) {
    return h('div', {}, [
      h('div', { class: 'card-label' }, label),
      h('div', { class: 'field-row' }, [
        h('input', {
          value: s.profileEdit.draft,
          oninput: (e) => setProfileEditDraft(e.target.value),
          onkeydown: (e) => { if (e.key === 'Enter') confirmProfileEdit(); if (e.key === 'Escape') cancelProfileEdit(); }
        }),
        h('button', { class: 'btn btn-primary btn-sm', onclick: confirmProfileEdit }, 'Save'),
        h('button', { class: 'btn btn-secondary btn-sm', onclick: cancelProfileEdit }, 'Cancel')
      ]),
      s.profileEdit.error ? h('div', { class: 'field-error' }, s.profileEdit.error) : null
    ]);
  }
  const value = field === 'name' ? p.name : (p.email || '—');
  return h('div', {}, [
    h('div', { class: 'card-label' }, label),
    h('div', { class: 'flex-between items-center' }, [
      h('span', {}, value),
      h('button', { class: 'icon-btn', 'aria-label': `Edit ${label}`, onclick: () => startProfileEdit(p.id, field) },
        h('span', { html: iconMarkup('pencil', { size: 14 }) }))
    ])
  ]);
}

function avatarPicker(p) {
  return h('div', {}, [
    h('div', { class: 'card-label' }, 'Avatar'),
    h('div', { class: 'flex-gap-sm avatar-picker' }, CONFIG.AVATARS.map((a) =>
      h('button', {
        class: 'icon-btn-plain avatar-option' + (a === p.avatar ? ' avatar-option-selected' : ''),
        'aria-label': a, 'aria-pressed': a === p.avatar ? 'true' : 'false',
        onclick: () => setProfileAvatar(p.id, a)
      }, h('span', { html: iconMarkup(a, { size: 20, color: a === p.avatar ? '#4D4DFF' : '#757993' }) }))
    ))
  ]);
}

export function renderSettings() {
  const s = getState();
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) || s.profiles[0];

  const currencySelect = h('select', {
    onchange: (e) => setState({ reportingCurrency: e.target.value })
  }, CONFIG.G20.map((cc) => h('option', { value: cc, selected: cc === s.reportingCurrency || undefined }, cc)));

  const themeSelect = h('select', { onchange: (e) => setTheme(e.target.value) }, [
    h('option', { value: 'system', selected: s.theme === 'system' || undefined }, 'Match device'),
    h('option', { value: 'light', selected: s.theme === 'light' || undefined }, 'Light'),
    h('option', { value: 'dark', selected: s.theme === 'dark' || undefined }, 'Dark')
  ]);

  const stablecoinOptions = Object.keys(CONFIG.CHAINS);
  const defaultCcySelect = h('select', { onchange: (e) => setDefaultStablecoin(e.target.value) },
    stablecoinOptions.map((c) => h('option', { value: c, selected: c === s.defaultStablecoin || undefined }, c)));
  const defaultChainOptions = CONFIG.CHAINS[s.defaultStablecoin] || [];
  const defaultChainSelect = h('select', { onchange: (e) => setDefaultChain(e.target.value) },
    defaultChainOptions.map((c) => h('option', { value: c, selected: c === s.defaultChain || undefined }, c)));

  const primaryKey = s.privateKeys[0];

  return h('div', { class: 'screen' }, [
    h('h1', { class: 'screen-title' }, 'Settings'),

    profile ? h('div', { class: 'card stack' }, [
      h('div', { class: 'card-title-sm' }, 'Profile'),
      profileField(s, profile, 'name', 'Name'),
      !profile.anonymous ? profileField(s, profile, 'email', 'Email') : null,
      avatarPicker(profile)
    ]) : null,

    h('div', { class: 'card' }, [
      h('div', { class: 'card-label' }, 'Reporting currency'),
      currencySelect
    ]),
    h('div', { class: 'card' }, [
      h('div', { class: 'card-label' }, 'Appearance'),
      themeSelect
    ]),
    h('div', { class: 'card stack' }, [
      h('div', { class: 'card-label' }, 'Default stablecoin'),
      defaultCcySelect,
      h('div', { class: 'card-label mt-sm' }, 'Default chain'),
      defaultChainSelect
    ]),

    h('div', { class: 'card flex-between items-center' }, [
      h('div', { class: 'card-label' }, 'Testnet'),
      toggleButton('Testnet', s.testnetEnabled, () => setTestnetEnabled(!s.testnetEnabled))
    ]),

    h('div', { class: 'card flex-between items-center' }, [
      h('div', { class: 'card-label' }, 'llms.txt'),
      h('div', { class: 'flex-gap-sm' }, [
        h('a', {
          class: 'icon-btn-plain', 'aria-label': 'Download llms.txt',
          href: '/llms.txt', download: 'llms.txt'
        }, h('span', { html: iconMarkup('fileText', { size: 18, color: '#757993' }) })),
        h('button', {
          class: 'icon-btn-plain', 'aria-label': 'Copy llms.txt', onclick: copyLlmsTxt
        }, h('span', { html: iconMarkup('clipboardCopy', { size: 18, color: '#757993' }) }))
      ])
    ]),

    h('div', { class: 'card stack' }, [
      h('div', { class: 'flex-gap-sm items-center' }, [
        h('div', { class: 'card-label' }, 'Account Recovery'),
        h('button', {
          class: 'icon-btn-plain', 'aria-label': 'What is Account Recovery?', onclick: toggleRecoveryInfo
        }, h('span', { html: iconMarkup('circleQuestionMark', { size: 14, color: '#757993' }) }))
      ]),
      s.showRecoveryInfo ? h('div', { class: 'text-sm text-muted' },
        'Your recovery phrase (mnemonic) is the only way to restore this wallet on another device. ' +
        'Tynti is self-custody: we never see or store it, and nobody can recover it for you if it’s ' +
        'lost. Never share it with anyone — write it down and keep it somewhere safe offline.'
      ) : null,
      primaryKey ? (primaryKey.revealed && primaryKey.words
        ? h('div', { class: 'word-grid' },
            primaryKey.words.map((w, i) => h('div', { class: 'word' }, [h('span', { class: 'n' }, `${i + 1}.`), ` ${w}`])))
        : null
      ) : null,
      h('div', { class: 'btn-row' }, [
        h('button', { class: 'btn btn-secondary', onclick: openImportKey }, 'Import'),
        primaryKey ? h('button', {
          class: 'btn btn-primary',
          onclick: () => togglePrivateKey(primaryKey.id)
        }, primaryKey.revealed ? 'Hide' : 'Reveal') : null
      ])
    ]),

    h('button', { class: 'btn btn-secondary text-muted', onclick: goLock }, 'Lock')
  ]);
}
